import { Component, OnInit, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CustomerService } from '../../../core/data-services/customer.service';
import { FieldService } from '../../../core/data-services/field.service';
import { Customer } from '../../../core/data-models/customer.model';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { DateFormatPipe, PhoneFormatPipe } from '../../../shared/pipes';

@Component({
  selector: 'app-customer-details',
  standalone: true,
  imports: [CommonModule, RouterLink, ConfirmModalComponent, DateFormatPipe, PhoneFormatPipe],
  templateUrl: './customer-details.component.html',
  styleUrls: ['./customer-details.component.scss']
})
export class CustomerDetailsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private customerService = inject(CustomerService);
  private fieldService = inject(FieldService);

  // State
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);
  customerId = signal<string | null>(null);
  customer = signal<Customer | null>(null);

  // Computed: fields belonging to this customer
  readonly customerFields = computed(() => {
    const custId = this.customerId();
    if (!custId) return [];
    return this.fieldService.getFieldsByCustomerId(custId);
  });

  // Computed stats
  readonly fieldCount = computed(() => this.customerFields().length);
  readonly totalAcreage = computed(() => {
    return this.customerFields().reduce((sum: number, f: any) => sum + (f.acreage || 0), 0);
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.customerId.set(id);
      this.loadCustomer(id);
    } else {
      this.errorMessage.set('No customer ID provided');
      this.isLoading.set(false);
    }
  }

  private async loadCustomer(id: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    // The cached list holds one page; a customer outside it is fetched directly.
    const found = this.customerService.getCustomerById(id) ?? (await this.customerService.fetchCustomerById(id));
    if (found) {
      this.customer.set(found);
    } else {
      this.errorMessage.set(this.customerService.error() ?? 'Customer not found');
    }
    this.isLoading.set(false);
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/customers']);
  }

  editCustomer(): void {
    const id = this.customerId();
    if (id) {
      this.router.navigate(['/customers', id, 'edit']);
    }
  }

  addField(): void {
    const id = this.customerId();
    if (id) {
      // Navigate to add field with customer pre-selected
      this.router.navigate(['/logbook/fields/add'], { queryParams: { customerId: id } });
    }
  }

  // Delete functionality
  confirmDelete(): void {
    const cust = this.customer();
    if (cust) {
      this.confirmModal.open({
        title: 'Delete Customer',
        message: `Are you sure you want to delete "${cust.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonClass: 'btn-danger',
        icon: 'bi-trash'
      });
    }
  }

  onDeleteConfirmed(): void {
    const id = this.customerId();
    if (id) {
      void this.deleteAndLeave(id);
    }
  }

  private async deleteAndLeave(id: string): Promise<void> {
    if (await this.customerService.deleteCustomer(id)) {
      this.router.navigate(['/customers']);
    } else {
      this.errorMessage.set(this.customerService.error() ?? 'Failed to delete customer');
    }
  }

  onDeleteCancelled(): void {
    // No action needed
  }

  // Helper: Format full address
  getFullAddress(): string {
    const cust = this.customer();
    if (!cust) return '';

    const parts = [cust.address, cust.city, cust.state, cust.zip].filter(Boolean);
    return parts.join(', ');
  }

  // Helper: Get Google Maps link
  getMapsUrl(): string {
    const address = this.getFullAddress();
    if (!address) return '';
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }

  // Helper: Check if address exists
  hasAddress(): boolean {
    const cust = this.customer();
    return !!(cust && (cust.address || cust.city || cust.state || cust.zip));
  }
}
